import { safeAbsurd } from '@ipfabric/utils/types';
import type { FC, Ref } from 'react';
import { getAccessibleTabIndex } from '../Button/ariaHelpers';
import { Icon } from '../Icon/Icon';
import { getCheckboxClassNames } from './Checkbox.styles';
import {
  type CheckboxProps,
  fromCheckboxToIconSize,
  fromValueToState,
} from './Checkbox.types';

export const Checkbox: FC<CheckboxProps> = ({
  value,
  size = 'default',
  disabled,
  onChange,
  className,
  hasError,
  innerRef,
}) => {
  const state = fromValueToState(value);

  const handleClick = () => {
    onChange?.(value === 'indeterminate' ? true : !value);
  };

  const iconSize = fromCheckboxToIconSize[size];

  const renderIcon = () => {
    switch (value) {
      case true:
        return <Icon glyph='fa-check' size={iconSize} />;
      case false:
        // use an invisible icon to make sure that the sizing and line-height don't change for unchecked state
        return <Icon glyph='fa-period' size={iconSize} className='invisible' />;
      case 'indeterminate':
        return <Icon glyph='fa-minus' size={iconSize} />;
      default:
        safeAbsurd(value, 'unknown checkbox state');
        return null;
    }
  };

  return onChange ? (
    <button
      ref={innerRef as Ref<HTMLButtonElement>}
      type='button'
      className={getCheckboxClassNames(
        state,
        size,
        disabled,
        hasError,
        className,
      )}
      disabled={disabled}
      onClick={handleClick}
      tabIndex={getAccessibleTabIndex(disabled)}
    >
      {renderIcon()}
    </button>
  ) : (
    <div
      ref={innerRef as Ref<HTMLDivElement>}
      className={getCheckboxClassNames(
        state,
        size,
        disabled,
        hasError,
        className,
      )}
    >
      {renderIcon()}
    </div>
  );
};
